/**
 * Time-series animation — drives a slider and play button over the unique
 * temporal values found in any visible vector layer.
 *
 * Detection rules (auto): a column scores high if its NAME looks temporal
 * (year/date/month/timestamp/…) AND its VALUES parse as either dates or
 * numbers in a year-like range (1800–2200) or month-like range (1–12).
 *
 * Each step applies a MapLibre `setFilter(['==', ['to-string', ['get', col]], v])`
 * to the user layer so only matching features render — works for points,
 * lines, and polygons.
 */
import { APP } from '../state.js';
import { toast } from '../ui/toast.js';

let userLayersRef = null;
let activeTS = null;        // { layerId, layerName, column, colMeta, values, currentIdx }

export function initTimeSeries(userLayers) {
  userLayersRef = userLayers;
}

// ---------------------------------------------------------------------------
// Public API — wired up to the toolbar / time bar buttons
// ---------------------------------------------------------------------------

export function toggleTimeSeries(map) {
  const bar = document.getElementById('timeBar');
  if (bar?.classList.contains('vis')) { deactivate(map); return; }
  activate(map);
}

export function playTimeSeries(map) {
  if (!activeTS) { activate(map); return; }
  const playBtn = document.getElementById('timePlay');
  if (APP.timeInterval) {
    clearInterval(APP.timeInterval); APP.timeInterval = null;
    playBtn.innerHTML = ICON_PLAY;
    return;
  }
  playBtn.innerHTML = ICON_PAUSE;
  APP.timeInterval = setInterval(() => {
    const next = (activeTS.currentIdx + 1) % activeTS.values.length;
    applyStep(map, next);
  }, 800);
}

// Legacy named export — kept so older imports don't break. The current bar
// drives setMonth via an internal slider listener; external callers can use
// applyStep directly if they need to.
export function setMonth(map, idx) {
  if (activeTS) applyStep(map, idx);
}

// ---------------------------------------------------------------------------
// Activation
// ---------------------------------------------------------------------------

function activate(map) {
  if (!userLayersRef) {
    toast('Time series not initialised yet — try again in a moment.');
    return;
  }
  const visible = userLayersRef.getVisibleVectorLayersWithFeatures();
  if (visible.length === 0) {
    toast('Time series needs a visible layer with loaded features. Add one to the map first.');
    return;
  }

  // Find the first visible layer that has at least one temporal column.
  let pick = null;
  for (const lyr of visible) {
    const cols = detectTemporalColumns(lyr.features);
    if (cols.length) { pick = { layer: lyr, column: cols[0] }; break; }
  }
  if (!pick) {
    toast('No date / year / month column detected on visible layers. Add a temporal field and try again.');
    return;
  }

  const { layer, column } = pick;
  const values = uniqueValues(layer.features, column.name, column);
  if (values.length < 2) {
    toast(`"${column.name}" only has one distinct value — nothing to animate.`);
    return;
  }

  activeTS = {
    layerId:    layer.id,
    layerName:  layer.name,
    column:     column.name,
    colMeta:    column,
    values,
    currentIdx: 0,
  };

  setupBar(map);
  document.getElementById('timeBar')?.classList.add('vis');
  applyStep(map, 0);
  toast(`Time series · ${layer.name} · ${column.name} (${values.length} steps)`);
}

function deactivate(map) {
  if (APP.timeInterval) { clearInterval(APP.timeInterval); APP.timeInterval = null; }
  if (activeTS) clearFilter(map, activeTS);
  activeTS = null;
  document.getElementById('timeBar')?.classList.remove('vis');
  const playBtn = document.getElementById('timePlay');
  if (playBtn) playBtn.innerHTML = ICON_PLAY;
}

// ---------------------------------------------------------------------------
// Slider wiring + per-step filter
// ---------------------------------------------------------------------------

function setupBar(map) {
  const slider = document.getElementById('timeSlider');
  const label  = document.getElementById('timeLabel');
  const title  = document.querySelector('.time-bar .time-title');
  if (!slider || !label) return;

  slider.min   = 0;
  slider.max   = activeTS.values.length - 1;
  slider.step  = 1;
  slider.value = 0;
  if (title) title.textContent = `${activeTS.layerName} · ${activeTS.column}`;
  label.textContent = activeTS.colMeta.format(activeTS.values[0]);

  slider.oninput = (e) => {
    if (APP.timeInterval) {
      clearInterval(APP.timeInterval); APP.timeInterval = null;
      const playBtn = document.getElementById('timePlay');
      if (playBtn) playBtn.innerHTML = ICON_PLAY;
    }
    applyStep(map, +e.target.value);
  };
}

function applyStep(map, idx) {
  if (!activeTS) return;
  activeTS.currentIdx = idx;
  const value = activeTS.values[idx];
  const slider = document.getElementById('timeSlider');
  const label  = document.getElementById('timeLabel');
  if (slider) slider.value = idx;
  if (label)  label.textContent = activeTS.colMeta.format(value);

  const entry = userLayersRef.loaded.get(activeTS.layerId);
  if (!entry) return;

  // Coerce both sides to string so int-vs-string property storage doesn't bite.
  const filterExpr = ['==', ['to-string', ['get', activeTS.column]], String(value)];
  for (const lid of entry.layerIds) {
    try { map.setFilter(lid, filterExpr); } catch { /* layer style might not accept a filter */ }
  }
}

function clearFilter(map, ts) {
  const entry = userLayersRef?.loaded.get(ts.layerId);
  if (!entry) return;
  for (const lid of entry.layerIds) {
    try { map.setFilter(lid, null); } catch { /* ignore */ }
  }
}

// ---------------------------------------------------------------------------
// Temporal column detection
// ---------------------------------------------------------------------------

const TEMPORAL_NAME_STRONG = /^(year|date|month|day|datetime|timestamp|created|updated|period|fiscal_year|yr|dt)$/i;
const TEMPORAL_NAME_PART   = /(year|date|month|day|time|timestamp|period|since|until|fiscal)/i;

export function detectTemporalColumns(features) {
  if (!features?.length) return [];
  const sample = features.slice(0, Math.min(features.length, 300));
  const props  = features[0]?.properties || {};
  const out = [];

  for (const key of Object.keys(props)) {
    const values = sample.map(f => f.properties?.[key]);
    const meta = inferTemporal(key, values);
    if (meta) out.push({ name: key, ...meta });
  }
  out.sort((a, b) => b.score - a.score);
  return out;
}

function inferTemporal(name, rawValues) {
  const values = rawValues.filter(v => v != null && v !== '');
  if (values.length === 0) return null;

  const lname = String(name).toLowerCase();
  let score = 0;
  if (TEMPORAL_NAME_STRONG.test(lname)) score += 5;
  else if (TEMPORAL_NAME_PART.test(lname)) score += 2;

  // 1) Numeric values — could be years or months.
  const allFinite = values.every(v => Number.isFinite(+v));
  if (allFinite) {
    const nums = values.map(v => +v);
    const min  = Math.min(...nums);
    const max  = Math.max(...nums);
    const allInt = nums.every(Number.isInteger);

    if (allInt && min >= 1800 && max <= 2200) {
      return {
        score: score + 5,
        kind:  'year',
        format: v => String(+v),
      };
    }
    if (allInt && min >= 1 && max <= 12 && /month|^mo$|^m$/i.test(lname)) {
      const monthNames = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
      return {
        score: score + 4,
        kind:  'month-num',
        format: v => monthNames[(+v) - 1] || String(v),
      };
    }
    // Numeric but not year/month — only accept if the name screams temporal.
    if (score >= 5) {
      return { score, kind: 'numeric', format: v => String(v) };
    }
    return null;
  }

  // 2) String values — try Date.parse on a sample.
  const parsable = values.filter(v => !Number.isNaN(Date.parse(v))).length;
  const ratio = parsable / values.length;
  if (ratio > 0.85) {
    return {
      score: score + 5,
      kind:  'date',
      format: v => {
        const d = new Date(v);
        if (Number.isNaN(+d)) return String(v);
        // Drop time if it's exactly midnight (looks like a date-only value).
        const iso = d.toISOString();
        return iso.endsWith('T00:00:00.000Z') ? iso.slice(0, 10) : iso.slice(0, 16).replace('T', ' ');
      },
    };
  }

  // Last resort: name strongly suggests temporal but values are categorical
  // (e.g. "Jan", "Feb"). Treat as ordered categorical.
  if (score >= 5) {
    return { score, kind: 'categorical', format: v => String(v) };
  }
  return null;
}

function uniqueValues(features, col, meta) {
  const seen = new Set();
  const out = [];
  for (const f of features) {
    const v = f.properties?.[col];
    if (v == null || v === '') continue;
    const key = String(v);
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(v);
  }
  if (meta.kind === 'date') {
    out.sort((a, b) => Date.parse(a) - Date.parse(b));
  } else if (meta.kind === 'year' || meta.kind === 'month-num' || meta.kind === 'numeric') {
    out.sort((a, b) => (+a) - (+b));
  } else {
    out.sort();
  }
  return out;
}

// ---------------------------------------------------------------------------
// Inline icons (kept in JS so the toolbar template stays static).
// ---------------------------------------------------------------------------

const ICON_PLAY  = '<svg viewBox="0 0 16 16"><polygon points="4,2 14,8 4,14"/></svg>';
const ICON_PAUSE = '<svg viewBox="0 0 16 16"><rect x="3" y="2" width="4" height="12"/><rect x="9" y="2" width="4" height="12"/></svg>';
